import { useCallback, useEffect, useState } from 'react';

import type { DatabaseEnvironmentConfig, EnvironmentSummary, Server } from '@/api/configuration';
import {
  createDatabaseEnvironmentConfig,
  deleteDatabaseEnvironmentConfig,
  listDatabaseEnvironmentConfigs,
  listServers,
  updateDatabaseEnvironmentConfig,
} from '@/api/configuration';
import { useEnvironments } from '@/hooks/use-configuration';
import { useToastStore } from '@/stores/toast-store';

// --- Environment Config Form ---

interface EnvConfigFormProps {
  projectKey: string;
  databaseId: string;
  environment: EnvironmentSummary;
  existing?: DatabaseEnvironmentConfig;
  servers: Server[];
  onSaved: () => void;
  onCancel: () => void;
}

function EnvironmentConfigForm({
  projectKey,
  databaseId,
  environment,
  existing,
  servers,
  onSaved,
  onCancel,
}: EnvConfigFormProps) {
  const [host, setHost] = useState(existing?.host ?? '');
  const [port, setPort] = useState<string>(existing?.port != null ? String(existing.port) : '');
  const [dbName, setDbName] = useState(existing?.databaseName ?? '');
  const [username, setUsername] = useState(existing?.username ?? '');
  const [password, setPassword] = useState(existing?.password ?? '');
  const [sshTunnelServerId, setSshTunnelServerId] = useState(existing?.sshTunnelServerId ?? '');
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<{ port?: string }>({});
  const addToast = useToastStore((s) => s.addToast);

  const validate = (): boolean => {
    const newErrors: { port?: string } = {};
    if (port && (isNaN(Number(port)) || Number(port) < 1 || Number(port) > 65535)) {
      newErrors.port = 'Port must be between 1 and 65535';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    const payload: Partial<DatabaseEnvironmentConfig> = {
      environmentId: environment.id,
      host: host.trim() || undefined,
      port: port ? Number(port) : undefined,
      databaseName: dbName.trim() || undefined,
      username: username.trim() || undefined,
      password: password || undefined,
      sshTunnelServerId: sshTunnelServerId || undefined,
    };

    setSaving(true);
    try {
      if (existing) {
        await updateDatabaseEnvironmentConfig(projectKey, databaseId, existing.id, payload);
        addToast({ type: 'success', message: `Environment config updated for ${environment.name}`, duration: 5000 });
      } else {
        await createDatabaseEnvironmentConfig(projectKey, databaseId, payload);
        addToast({ type: 'success', message: `Environment config created for ${environment.name}`, duration: 5000 });
      }
      onSaved();
    } catch (err: unknown) {
      const status = (err as { status?: number })?.status;
      if (status === 409) {
        addToast({ type: 'error', message: `A configuration already exists for ${environment.name}. Please refresh and try again.`, duration: 8000 });
      } else {
        addToast({ type: 'error', message: `Failed to save environment config for ${environment.name}`, duration: 5000 });
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3 pt-2">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {/* Host */}
        <div>
          <label htmlFor={`env-host-${environment.id}`} className="block text-xs font-medium text-content-secondary mb-1">
            Host
          </label>
          <input
            id={`env-host-${environment.id}`}
            type="text"
            value={host}
            onChange={(e) => setHost(e.target.value)}
            placeholder="e.g. db.example.com"
            className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600"
          />
        </div>

        {/* Port */}
        <div>
          <label htmlFor={`env-port-${environment.id}`} className="block text-xs font-medium text-content-secondary mb-1">
            Port
          </label>
          <input
            id={`env-port-${environment.id}`}
            type="number"
            value={port}
            onChange={(e) => setPort(e.target.value)}
            placeholder="e.g. 3306"
            min={1}
            max={65535}
            className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600"
          />
          {errors.port && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.port}</p>}
        </div>

        {/* Database Name */}
        <div>
          <label htmlFor={`env-dbname-${environment.id}`} className="block text-xs font-medium text-content-secondary mb-1">
            Database Name
          </label>
          <input
            id={`env-dbname-${environment.id}`}
            type="text"
            value={dbName}
            onChange={(e) => setDbName(e.target.value)}
            placeholder="e.g. corvus_db"
            className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600"
          />
        </div>

        {/* Username */}
        <div>
          <label htmlFor={`env-username-${environment.id}`} className="block text-xs font-medium text-content-secondary mb-1">
            Username
          </label>
          <input
            id={`env-username-${environment.id}`}
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="e.g. db_admin"
            className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600"
          />
        </div>

        {/* Password */}
        <div>
          <label htmlFor={`env-password-${environment.id}`} className="block text-xs font-medium text-content-secondary mb-1">
            Password
          </label>
          <div className="relative">
            <input
              id={`env-password-${environment.id}`}
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
              className="w-full rounded-md border border-border bg-surface px-3 py-1.5 pr-10 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-content-tertiary hover:text-content-secondary focus:outline-none focus:ring-2 focus:ring-brand-600"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? (
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                </svg>
              ) : (
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* SSH Tunnel Server */}
        <div>
          <label htmlFor={`env-ssh-${environment.id}`} className="block text-xs font-medium text-content-secondary mb-1">
            SSH Tunnel Server
          </label>
          <select
            id={`env-ssh-${environment.id}`}
            value={sshTunnelServerId}
            onChange={(e) => setSshTunnelServerId(e.target.value)}
            className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-brand-600"
          >
            <option value="">None (direct connection)</option>
            {servers.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}{s.ansibleHost ? ` (${s.ansibleHost})` : ''}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="flex gap-2 pt-1">
        <button
          type="submit"
          disabled={saving}
          className="rounded-md bg-brand-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-800 focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? 'Saving...' : existing ? 'Update' : 'Save'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-content-secondary hover:bg-surface-raised focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-2"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

// --- Environment Row ---

interface EnvironmentRowProps {
  projectKey: string;
  databaseId: string;
  environment: EnvironmentSummary;
  config?: DatabaseEnvironmentConfig;
  onRefresh: () => void;
}

function EnvironmentRow({ projectKey, databaseId, environment, config, onRefresh }: EnvironmentRowProps) {
  const [editing, setEditing] = useState(false);
  const [servers, setServers] = useState<Server[]>([]);
  const [loadingServers, setLoadingServers] = useState(false);
  const addToast = useToastStore((s) => s.addToast);

  const loadServers = useCallback(async () => {
    setLoadingServers(true);
    try {
      const data = await listServers(projectKey, environment.id);
      setServers(data);
    } catch {
      // Servers list is optional — silently default to empty
      setServers([]);
    } finally {
      setLoadingServers(false);
    }
  }, [projectKey, environment.id]);

  useEffect(() => {
    if (editing) {
      loadServers();
    }
  }, [editing, loadServers]);

  const handleDelete = async () => {
    if (!config) return;
    if (!confirm(`Delete environment config for "${environment.name}"?`)) return;
    try {
      await deleteDatabaseEnvironmentConfig(projectKey, databaseId, config.id);
      addToast({ type: 'success', message: `Environment config deleted for ${environment.name}`, duration: 5000 });
      onRefresh();
    } catch {
      addToast({ type: 'error', message: `Failed to delete environment config for ${environment.name}`, duration: 5000 });
    }
  };

  const handleSaved = () => {
    setEditing(false);
    onRefresh();
  };

  return (
    <div className="border-b border-border last:border-b-0 px-3 py-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-content">{environment.name}</span>
          {config ? (
            <span className="inline-flex items-center rounded-full bg-green-100 dark:bg-green-900/30 px-2 py-0.5 text-xs font-medium text-green-700 dark:text-green-300">
              Configured
            </span>
          ) : (
            <span className="inline-flex items-center rounded-full bg-gray-100 dark:bg-gray-800 px-2 py-0.5 text-xs font-medium text-content-tertiary">
              Not configured
            </span>
          )}
          {config?.host && (
            <span className="text-xs text-content-tertiary font-mono">
              {config.host}{config.port ? `:${config.port}` : ''}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!editing && (
            <>
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="rounded px-2 py-1 text-xs font-medium text-content-secondary hover:bg-surface-raised focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-1"
              >
                {config ? 'Edit' : 'Configure'}
              </button>
              {config && (
                <button
                  type="button"
                  onClick={handleDelete}
                  className="rounded px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1"
                >
                  Delete
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {editing && (
        loadingServers ? (
          <div className="pt-2">
            <div className="animate-pulse h-8 rounded bg-gray-200 dark:bg-gray-700" />
          </div>
        ) : (
          <EnvironmentConfigForm
            projectKey={projectKey}
            databaseId={databaseId}
            environment={environment}
            existing={config}
            servers={servers}
            onSaved={handleSaved}
            onCancel={() => setEditing(false)}
          />
        )
      )}
    </div>
  );
}

// --- Main Section Component ---

interface DatabaseEnvironmentConfigSectionProps {
  projectKey: string;
  databaseId: string;
  databaseName: string;
}

export function DatabaseEnvironmentConfigSection({
  projectKey,
  databaseId,
  databaseName,
}: DatabaseEnvironmentConfigSectionProps) {
  const { data: environments, isLoading: loadingEnvs } = useEnvironments(projectKey);
  const [envConfigs, setEnvConfigs] = useState<DatabaseEnvironmentConfig[]>([]);
  const [loadingConfigs, setLoadingConfigs] = useState(true);

  const loadEnvConfigs = useCallback(async () => {
    if (!databaseId) return;
    setLoadingConfigs(true);
    try {
      const data = await listDatabaseEnvironmentConfigs(projectKey, databaseId);
      setEnvConfigs(data);
    } catch {
      setEnvConfigs([]);
    } finally {
      setLoadingConfigs(false);
    }
  }, [projectKey, databaseId]);

  useEffect(() => {
    loadEnvConfigs();
  }, [loadEnvConfigs]);

  const getConfigForEnvironment = (envId: string) =>
    envConfigs.find((c) => c.environmentId === envId);

  const isLoading = loadingEnvs || loadingConfigs;

  return (
    <div className="mt-3 rounded-lg border border-border bg-surface-raised">
      <div className="px-3 py-2 border-b border-border">
        <h4 className="text-xs font-semibold text-content-secondary uppercase tracking-wide">
          Environment Configuration — {databaseName}
        </h4>
        <p className="mt-0.5 text-xs text-content-tertiary">
          Configure connection details per environment. Values here override the database definition defaults.
        </p>
      </div>

      {isLoading && (
        <div className="p-3 animate-pulse space-y-2">
          {[1, 2].map((i) => (
            <div key={i} className="h-8 rounded bg-gray-200 dark:bg-gray-700" />
          ))}
        </div>
      )}

      {!isLoading && (!environments || environments.length === 0) && (
        <div className="p-3 text-center">
          <p className="text-sm text-content-tertiary">
            No environments configured for this project. Add environments first.
          </p>
        </div>
      )}

      {!isLoading && environments && environments.length > 0 && (
        <div>
          {environments.map((env) => (
            <EnvironmentRow
              key={env.id}
              projectKey={projectKey}
              databaseId={databaseId}
              environment={env}
              config={getConfigForEnvironment(env.id)}
              onRefresh={loadEnvConfigs}
            />
          ))}
        </div>
      )}
    </div>
  );
}
